import { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { FaRegEdit } from "react-icons/fa";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { IPincodeWeather, IRainSettings, WeatherFilter } from "./interface";
import { usePincodeWeatherAPI } from "./usePincodeWeatherAPI";
import RainSettingsModal from "./RainSettingsModal";
import { useUtils } from "@/app/hooks/useUtils";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";


const WEATHER_FILTERS: { key: WeatherFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "rainy", label: "Rainy" },
  { key: "normal", label: "Normal" },
];

const RainyIcon = () => (
  <span className="relative flex h-6 w-6 items-center justify-center mr-0.5">
    <style>{`
      @keyframes badgeRain {
        0% { transform: translateY(-2px); opacity: 0; }
        30% { opacity: 1; }
        100% { transform: translateY(3px); opacity: 0; }
      }
      .badge-rain-drop {
        animation: badgeRain 0.8s infinite linear;
      }
    `}</style>
    <svg className="h-4 w-4 text-sky-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 17.58A5 5 0 0 0 18 8h-1.26A8 8 0 1 0 4 16.25" fill="#bae6fd" stroke="#0284c7" strokeWidth="1.5" />
      <line x1="9" y1="16" x2="8" y2="19" className="badge-rain-drop text-sky-600" style={{ animationDelay: "0.1s" }} />
      <line x1="13" y1="17" x2="12" y2="20" className="badge-rain-drop text-sky-600" style={{ animationDelay: "0.4s" }} />
      <line x1="17" y1="16" x2="16" y2="19" className="badge-rain-drop text-sky-600" style={{ animationDelay: "0.2s" }} />
    </svg>
  </span>
);

const SunIcon = () => (
  <span className="relative flex h-6 w-6 items-center justify-center mr-0.5">
    <style>{`
      @keyframes sunRotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .sun-icon-spin {
        animation: sunRotate 12s infinite linear;
      }
    `}</style>
    <svg className="h-4 w-4 text-amber-500 sun-icon-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" fill="#fef08a" stroke="#d97706" strokeWidth="1.5" />
      <line x1="12" y1="2" x2="12" y2="4" stroke="#d97706" />
      <line x1="12" y1="20" x2="12" y2="22" stroke="#d97706" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" stroke="#d97706" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" stroke="#d97706" />
      <line x1="2" y1="12" x2="4" y2="12" stroke="#d97706" />
      <line x1="20" y1="12" x2="22" y2="12" stroke="#d97706" />
      <line x1="6.34" y1="17.66" x2="4.93" y2="19.07" stroke="#d97706" />
      <line x1="19.07" y1="4.93" x2="17.66" y2="6.34" stroke="#d97706" />
    </svg>
  </span>
);

const WeatherBadge = ({ isRainy }: { isRainy: boolean }) =>
  isRainy ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-sky-50 to-blue-100/60 border border-sky-200 px-2.5 py-0.5 text-xs font-semibold text-sky-700 shadow-sm transition-all duration-300">
      <RainyIcon />
      Rainy
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 to-orange-50/60 border border-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-500 shadow-sm transition-all duration-300">
      <SunIcon />
      Normal
    </span>
  );

const PincodeWeatherSetup = () => {
  const { pincodeWeatherList, isListLoading, isSaving, fetchList, saveRainSettings } = usePincodeWeatherAPI();
  const { veticDateFormat } = useUtils();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [weatherFilter, setWeatherFilter] = useState<WeatherFilter>("all");
  const [selectedPincodes, setSelectedPincodes] = useState<Set<string>>(new Set());
  const [editingPincodes, setEditingPincodes] = useState<IPincodeWeather[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const search = searchTerm.trim();
      fetchList(search ? { search } : undefined);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm, fetchList]);

  const cityForm = useForm<{ city: string }>();

  const cityMenuItems = useMemo<ISelectMenuItemData[]>(() => {
    const cities = Array.from(new Set(pincodeWeatherList.map((item) => item.city).filter(Boolean))).sort();
    return [
      { title: "Select All", id: "" },
      ...cities.map((city) => ({ title: city, id: city })),
    ];
  }, [pincodeWeatherList]);

  const baseFilteredList = useMemo(
    () => pincodeWeatherList.filter((item) => !cityFilter || item.city === cityFilter),
    [pincodeWeatherList, cityFilter],
  );

  const visibleList = useMemo(
    () =>
      baseFilteredList.filter((item) =>
        weatherFilter === "all" ? true : weatherFilter === "rainy" ? item.isRainy : !item.isRainy,
      ),
    [baseFilteredList, weatherFilter],
  );

  const { allCount, rainyCount, normalCount } = useMemo(() => {
    let rainy = 0;
    let normal = 0;
    baseFilteredList.forEach((item) => {
      if (item.isRainy) rainy++;
      else normal++;
    });
    return {
      allCount: baseFilteredList.length,
      rainyCount: rainy,
      normalCount: normal,
    };
  }, [baseFilteredList]);

  const selectedList = useMemo(
    () => pincodeWeatherList.filter((item) => selectedPincodes.has(item.pincode)),
    [pincodeWeatherList, selectedPincodes],
  );

  const isAllVisibleSelected = visibleList.length > 0 && visibleList.every((item) => selectedPincodes.has(item.pincode));

  const toggleRow = useCallback((pincode: string, checked: boolean) => {
    setSelectedPincodes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(pincode);
      else next.delete(pincode);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback((checked: boolean) => {
    setSelectedPincodes((prev) => {
      const next = new Set(prev);
      visibleList.forEach((item) => {
        if (checked) next.add(item.pincode);
        else next.delete(item.pincode);
      });
      return next;
    });
  }, [visibleList]);

  const clearSelection = () => setSelectedPincodes(new Set());

  const handleSave = async (pincodes: string[], settings: IRainSettings) => {
    const success = await saveRainSettings(pincodes, settings);
    if (success) {
      clearSelection();
      fetchList();
    }
    return success;
  };

  const columns = useMemo<TableColumn<IPincodeWeather>[]>(
    () => [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            checked={isAllVisibleSelected}
            onChange={(e) => toggleAllVisible(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#193A6A] focus:ring-[#193A6A]"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedPincodes.has(row.pincode)}
            onChange={(e) => toggleRow(row.pincode, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#193A6A] focus:ring-[#193A6A]"
          />
        ),
        align: "center",
        width: 50,
      },
      {
        id: "pincode",
        header: "Pincode",
        accessor: "pincode",
        className: "font-semibold text-gray-900",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "latitude",
        header: "Latitude",
        accessor: (row) => row.latitude || "-",
        className: "text-gray-500",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "longitude",
        header: "Longitude",
        accessor: (row) => row.longitude || "-",
        className: "text-gray-500",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "city",
        header: "City",
        accessor: "city",
        className: "text-gray-500",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "weather",
        header: "Weather",
        cell: ({ row }) => <WeatherBadge isRainy={row.isRainy} />,
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "is_raining_updated_at",
        header: "Last Updated At",
        cell: ({ row }) => (row.isRainingUpdatedAt ? veticDateFormat(row.isRainingUpdatedAt) : "—"),
        className: "text-gray-500",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "rainWindow",
        header: "Override Window",
        cell: ({ row }) => {
          const timeWindow = row.startTime && row.endTime ? `${row.startTime} – ${row.endTime}` : "—";
          return row.isEventActive ? (
            <div className="flex flex-col">
              <span>Others</span>
              <span className="text-gray-500">{timeWindow}</span>
            </div>
          ) : (
            timeWindow
          );
        },
        className: "text-gray-500",
        headerClassName: "text-[#193A6A]",
      },
      {
        id: "edit",
        header: "Action",
        align: "center",
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <button
              onClick={() => setEditingPincodes([row])}
              className="text-[#193A6A] hover:text-[#132c52] transition-colors p-1.5 hover:bg-gray-150 rounded-lg"
            >
              <FaRegEdit className="w-4 h-4" />
            </button>
          </div>
        ),
        width: 80,
        headerClassName: "text-[#193A6A] text-center",
      },

    ],
    [isAllVisibleSelected, toggleAllVisible, selectedPincodes, toggleRow, veticDateFormat],
  );

  return (
    <div className="px-5 py-3">
      <div className="sm:flex sm:items-center justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">Pincode Weather (Rain) Setup</h1>
          <p className="mt-2 text-sm text-gray-500">
            Set the weather condition (Normal or Rainy) for each pincode. Select multiple pincodes to bulk edit.
          </p>
        </div>
        <div className="mt-4 flex items-center gap-3 sm:mt-0 sm:flex-none">
          <input
            type="text"
            inputMode="numeric"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.replace(/[^0-9]/g, ""))}
            maxLength={6}
            placeholder="Search Pincode"
            className="h-[38px] w-[220px] rounded-lg border border-gray-300 px-3 text-xs text-slate-700 focus:border-[#193A6A] focus:outline-none"
          />
          <div className="w-[220px]">
            <NonSearchableSelectMenu
              selectItems={cityMenuItems}
              onSelectItem={(item) => setCityFilter(item?.id ?? "")}
              defaultSelected={cityMenuItems.find((item) => item.id === cityFilter) || null}
              placeholder="Select City"
              className="text-xs"
              showDropdownIcon
              dropdownPosition="bottom"
              listZIndex="z-50"
              errorMessages={[]}
              fieldError={cityForm.formState.errors.city}
              register={cityForm.register("city")}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 text-xs">
        <span className="text-slate-500 font-medium">Filter weather:</span>
        {WEATHER_FILTERS.map(({ key, label }) => {
          let displayLabel = label;
          if (key === "rainy") {
            displayLabel = `Rainy (${rainyCount})`;
          } else if (key === "normal") {
            displayLabel = `Normal (${normalCount})`;
          } else if (key === "all") {
            displayLabel = `All (${allCount})`;
          }

          return (
            <button
              key={key}
              onClick={() => setWeatherFilter(key)}
              className={`rounded-full border px-3 py-1 font-medium transition-all ${weatherFilter === key
                ? "border-[#193A6A] bg-[#EEF5FF] text-[#193A6A]"
                : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
            >
              {displayLabel}
            </button>
          );
        })}
      </div>

      {selectedPincodes.size > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-xl bg-gray-50 border border-gray-200 px-6 py-3 shadow-sm">
          <div className="text-sm text-gray-700">
            <span className="font-semibold text-[#193A6A]">{selectedPincodes.size}</span> pincode(s) selected
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={() => setEditingPincodes(selectedList)} className={PRIMARY_BUTTON}>
              Bulk Rain Settings
            </button>
            <button onClick={clearSelection} className={SECONDARY_BUTTON}>
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {isListLoading ? (
        <div className="mt-5">
          <StructuredTableSkeleton columns={columns.length} />
        </div>
      ) : visibleList.length > 0 ? (
        <div className="mt-5">
          <TableComponent<IPincodeWeather>
            columns={columns}
            data={visibleList}
            getRowId={(row) => row.pincode}
            containerClassName="border border-gray-200 shadow-sm rounded-2xl mr-0 bg-white"
            scrollContainerClassName="overflow-x-auto mt-0 max-h-[calc(100vh-320px)]"
            minTableWidthClassName="min-w-full"
            tableClassName="min-w-full divide-y divide-gray-200"
            stickyHeader={true}
            wrapCells={true}
          />
        </div>
      ) : (
        <NoDataFoundComponent
          message="No pincodes match the selected filters"
          description="Try changing the city or weather filter."
        />
      )}

      <RainSettingsModal
        open={editingPincodes.length > 0}
        pincodes={editingPincodes}
        isSaving={isSaving}
        onClose={() => setEditingPincodes([])}
        onSave={handleSave}
      />
    </div>
  );
};

export default PincodeWeatherSetup;
